import { useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '@/lib/authService';

export interface ResolvedStatusEntry {
  work_item_id: string;
  item_type: string | null;
  item_subtype: string | null;
  raw_status: string;
  base_status: string;
  resolved_status: string;
  resolved_label: string;
  source: string;
  timing_status: string | null;
  is_terminal: boolean;
  is_operationally_complete: boolean;
  workflow_state: string | null;
  status_changed_at: string | null;
}

interface UseWorkItemsResolvedStatusResult {
  statuses: Record<string, ResolvedStatusEntry>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const BATCH_SIZE = 100;

/**
 * Fetch resolved statuses for multiple work items using the batch endpoint.
 * This is the list-view equivalent of useWorkItemStatus (single item).
 */
export function useWorkItemsResolvedStatus(
  workItemIds: string[],
  options: { enabled?: boolean; refreshIntervalMs?: number } = {}
): UseWorkItemsResolvedStatusResult {
  const { enabled = true, refreshIntervalMs } = options;
  const [statuses, setStatuses] = useState<Record<string, ResolvedStatusEntry>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchStatuses = useCallback(async () => {
    const ids = workItemIds.filter(Boolean);
    if (!enabled || ids.length === 0) {
      setStatuses({});
      setError(null);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const token = authService.getTokens()?.access_token;
      const allResults: Record<string, ResolvedStatusEntry> = {};

      // Process in batches to avoid overly large requests
      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        if (controller.signal.aborted) return;

        const batch = ids.slice(i, i + BATCH_SIZE);
        const response = await fetch('/api/v1/work-items/status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ ids: batch }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || `Batch status failed (${response.status})`);
        }

        const data = await response.json();
        if (data.success && data.data) {
          Object.assign(allResults, data.data as Record<string, ResolvedStatusEntry>);
        }
      }

      setStatuses(allResults);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load statuses');
    } finally {
      setLoading(false);
    }
  }, [workItemIds, enabled]);

  useEffect(() => {
    void fetchStatuses();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchStatuses]);

  useEffect(() => {
    if (!refreshIntervalMs || refreshIntervalMs <= 0) return;
    const interval = setInterval(() => {
      void fetchStatuses();
    }, refreshIntervalMs);
    return () => clearInterval(interval);
  }, [fetchStatuses, refreshIntervalMs]);

  return {
    statuses,
    loading,
    error,
    refetch: fetchStatuses,
  };
}
